<script setup>
import FirebaseMessaging from "../components/FirebaseMessaging.vue"
import {useAppStore} from "../stores/app.js"
import {useUserStore} from "../stores/user.js"
import {useUserRolesStore} from "../stores/userRoles"
import {useSiteStore} from "../stores/site.js"
import {useSitesStore} from "../stores/sites.js"
import {useOrgStore} from "../stores/org"
import {useOrgSpacesStore} from "../stores/orgSpaces"
import {useOrgSpacesEnvironmentsStore} from "../stores/orgSpacesEnvironments"
import {storeToRefs} from "pinia"
import {watch, ref, computed, onMounted} from "vue"
import {OneViewAdmin, OneButton} from "@jirizavadil/framework.one/ui"
import Logo from "../assets/img/logo/platformaone_logo.svg?url"
import LogoWhite from "../assets/img/logo/platformaone_logo_white.svg?url"
import LogoIcon from "../assets/img/logo/platformaone_icon.svg?url"
import LogoIconWhite from "../assets/img/logo/platformaone_icon_white.svg?url"
import {useRoute, useRouter} from "vue-router"
import {useAuth} from "@/composables/auth"
import langs from "../langs/index.js"
import ColorSchemeToggle from "../components/ColorSchemeToggle.vue"

const route = useRoute()
const router = useRouter()

const auth = useAuth()
const {allowed, logout} = auth

const appStore = useAppStore()
const userStore = useUserStore()
const userRolesStore = useUserRolesStore()
const siteStore = useSiteStore()
const sitesStore = useSitesStore()
const orgStore = useOrgStore()
const orgSpacesStore = useOrgSpacesStore()
const orgSpacesEnvironmentsStore = useOrgSpacesEnvironmentsStore()

const {app} = storeToRefs(appStore)
const {user, userAuth, userLoaded, userSelectedSiteId} = storeToRefs(userStore)
const {isPlatformAdmin} = storeToRefs(userRolesStore)
const {site, siteName, siteLoaded} = storeToRefs(siteStore)
const {sitesSorted, sitesPinnedSorted, sitesUnpinnedSorted} = storeToRefs(sitesStore)
const {org, orgLoaded} = storeToRefs(orgStore)
const {spaces, orgSpacesLoaded} = storeToRefs(orgSpacesStore)
const {environments, orgEnvironmentsLoaded} = storeToRefs(orgSpacesEnvironmentsStore)

// const loaded = computed(
//   () =>
//     userLoaded.value &&
//     siteLoaded.value &&
//     ((app.value.orgId && orgLoaded.value) || !app.value.orgId) &&
//     ((app.value.spaceId && orgSpacesLoaded.value) || !app.value.spaceId) &&
//     ((app.value.environmentId && orgEnvironmentsLoaded.value) || !app.value.environmentId)
// )

const siteFeatures = computed(() => site.value?.features || {})

const loaded = computed(() => userLoaded.value && siteLoaded.value)

const isAllowedToUseLegacyEntitlementsVoucherTemplates = computed(() => {
  if (!site.value?.id) return false
  return [
    "8obBuNZBsaDlA88QhySX", // zrno zrnko
    "s6meSHPM0kdCcWyfMm6c", // molo lipno
  ].includes(site.value?.id)
})

const menuItems = computed(() => [
  {
    title: "dashboard",
    icon: "Home",
    route: {name: "DashboardView", params: {siteName: siteName.value}},
    views: ["DashboardView"],
    minimumPermission: "site",
  },
  {
    title: langs.get("view-title-commerce-orders"),
    icon: "Inbox",
    route: {name: "ShopOrdersView", params: {siteName: siteName.value}},
    views: ["ShopOrdersView", "ShopOrdersListView", "ShopOrderDetailView"],
    minimumPermission: "shop.orders.read",
  },
  {
    title: langs.get("view-title-commerce-carts"),
    icon: "ShoppingCart",
    route: {name: "ShopCartsView", params: {siteName: siteName.value}},
    views: ["ShopCartsView"],
    minimumPermission: "shop.carts.read",
  },
  ...(siteFeatures.value?.global?.balance
    ? [
        {
          title: langs.get("view-title-balances"),
          icon: "Wallet",
          route: {name: "BalancesView", params: {siteName: siteName.value}},
          views: [
            "BalancesView",
            "BalancesAddView",
            "BalancesDetailView",
            "BalancesDetailTransactView",
          ],
          // minimumPermission: "balance.read",
          minimumPermission: "shop.balance.balance.get",
        },
      ]
    : []),
  ...(siteFeatures.value?.global?.entitlement
    ? [
        {
          title: langs.get("view-title-commerce-entitlements"),
          icon: "CreditCard",
          route: {name: "EntitlementsView", params: {siteName: siteName.value}},
          views: [
            "EntitlementsView",
            "EntitlementsDetailView",
            "EntitlementsGiftVouchersView",
            "EntitlementsAddView",
          ],
          // minimumPermission: "platform",
          minimumPermission: "shop.entitlement.get",
        },
      ]
    : []),
  ...(siteFeatures.value?.global?.entitlement &&
  (isPlatformAdmin.value || isAllowedToUseLegacyEntitlementsVoucherTemplates.value)
    ? [
        {
          title: langs.get("view-title-commerce-voucher-templates"),
          icon: "CreditCard",
          route: {name: "EntitlementsVoucherTemplatesView", params: {siteName: siteName.value}},
          views: [
            "EntitlementsVoucherTemplatesView",
            "EntitlementsVoucherTemplateAddView",
            "EntitlementsVoucherTemplateDetailView",
          ],
          minimumPermission: "shop.entitlement.create",
        },
      ]
    : []),
  ...(siteFeatures.value?.global?.balance
    ? [
        {
          title: langs.get("view-title-identity-companies"),
          icon: "Building",
          route: {name: "IdentityCompaniesView", params: {siteName: siteName.value}},
          views: [
            "IdentityCompaniesView",
            "IdentityCompaniesAddView",
            "IdentityCompaniesDetailView",
          ],
          // minimumPermission: "identity.companies.read",
          minimumPermission: "shop.identity.company.get",
        },
        {
          title: langs.get("view-title-identity-persons"),
          icon: "Users",
          route: {name: "IdentityPersonsView", params: {siteName: siteName.value}},
          views: ["IdentityPersonsView", "IdentityPersonsAddView", "IdentityPersonsDetailView"],
          // minimumPermission: "identity.persons.read",
          minimumPermission: "shop.identity.person.get",
        },
      ]
    : []),
  ...(siteFeatures.value?.global?.loyalty
    ? [
        {
          title: langs.get("loyalty"),
          icon: "Gift",
          route: {name: "LoyaltyView", params: {siteName: siteName.value}},
          views: [
            "LoyaltyView",
            "LoyaltyDiscountsView",
            "LoyaltyDiscountsAddView",
            "LoyaltyDiscountsEditView",
            "LoyaltyDiscountsDetailView",
            "LoyaltyCashbacksView",
            "LoyaltyCashbacksAddView",
            "LoyaltyCashbacksEditView",
            "LoyaltyCashbacksDetailView",
            ...(siteFeatures.value?.global?.loyalty?.stampCard
              ? [
                  "LoyaltyStampCardsView",
                  "LoyaltyStampCardsAddView",
                  "LoyaltyStampCardsEditView",
                  "LoyaltyStampCardsDetailView",
                  "LoyaltyStampCardTrackDetailView",
                  "LoyaltyStampCardInstanceDetailView",
                ]
              : []),
            "LoyaltyTopupBonusesView",
            "LoyaltyTopupBonusesAddView",
            "LoyaltyTopupBonusesEditView",
            "LoyaltyTopupBonusesDetailView",
            "LoyaltyJoinPagesView",
            "LoyaltyJoinPagesAddView",
            "LoyaltyJoinPagesEditView",
            "LoyaltyJoinPagesDetailView",
            "LoyaltyTransactionsView",
          ],
          minimumPermission: "shop.loyalty.read",
          children: [
            ...(siteFeatures.value?.global?.loyalty?.discount
              ? [
                  {
                    title: langs.get("view-title-loyalty-discounts"),
                    icon: "Tag",
                    route: {name: "LoyaltyDiscountsView", params: {siteName: siteName.value}},
                    views: [
                      "LoyaltyDiscountsView",
                      "LoyaltyDiscountsAddView",
                      "LoyaltyDiscountsEditView",
                      "LoyaltyDiscountsDetailView",
                    ],
                    minimumPermission: "shop.loyalty.read",
                  },
                ]
              : []),
            ...(siteFeatures.value?.global?.loyalty?.cashback
              ? [
                  {
                    title: langs.get("view-title-loyalty-cashbacks"),
                    icon: "Coins",
                    route: {name: "LoyaltyCashbacksView", params: {siteName: siteName.value}},
                    views: [
                      "LoyaltyCashbacksView",
                      "LoyaltyCashbacksAddView",
                      "LoyaltyCashbacksEditView",
                      "LoyaltyCashbacksDetailView",
                    ],
                    minimumPermission: "shop.loyalty.read",
                  },
                ]
              : []),
            ...(siteFeatures.value?.global?.loyalty?.stampCard
              ? [
                  {
                    title: langs.get("view-title-loyalty-stamp-cards"),
                    icon: "LayoutGrid",
                    route: {name: "LoyaltyStampCardsView", params: {siteName: siteName.value}},
                    views: [
                      "LoyaltyStampCardsView",
                      "LoyaltyStampCardsAddView",
                      "LoyaltyStampCardsEditView",
                      "LoyaltyStampCardsDetailView",
                      "LoyaltyStampCardTrackDetailView",
                      "LoyaltyStampCardInstanceDetailView",
                    ],
                    minimumPermission: "shop.loyalty.read",
                  },
                ]
              : []),
            ...((siteFeatures.value?.global?.loyalty?.topupBonus ?? true)
              ? [
                  {
                    title: langs.get("view-title-loyalty-topup-bonuses"),
                    icon: "HandCoins",
                    route: {name: "LoyaltyTopupBonusesView", params: {siteName: siteName.value}},
                    views: [
                      "LoyaltyTopupBonusesView",
                      "LoyaltyTopupBonusesAddView",
                      "LoyaltyTopupBonusesEditView",
                      "LoyaltyTopupBonusesDetailView",
                    ],
                    minimumPermission: "shop.loyalty.read",
                  },
                ]
              : []),
            {
              title: langs.get("view-title-loyalty-join-pages"),
              icon: "UserPlus",
              route: {name: "LoyaltyJoinPagesView", params: {siteName: siteName.value}},
              views: [
                "LoyaltyJoinPagesView",
                "LoyaltyJoinPagesAddView",
                "LoyaltyJoinPagesEditView",
                "LoyaltyJoinPagesDetailView",
              ],
              minimumPermission: "shop.loyalty.read",
            },
            {
              title: langs.get("view-title-loyalty-transactions"),
              icon: "Receipt",
              route: {name: "LoyaltyTransactionsView", params: {siteName: siteName.value}},
              views: ["LoyaltyTransactionsView"],
              minimumPermission: "shop.loyalty.read",
            },
          ],
        },
      ]
    : []),
  {
    title: langs.get("view-title-booking"),
    icon: "CalendarCheck",
    route: {name: "BookingReservationsView", params: {siteName: siteName.value}},
    views: [
      "BookingView",
      "BookingReservationsView",
      "BookingReservationsAddView",
      "BookingReservationsDetailView",
      "BookingSlotsView",
      "BookingSlotsAddView",
      "BookingSlotsEditView",
      "BookingSlotsDetailView",
      "BookingServicesView",
      "BookingServicesAddView",
      "BookingServicesEditView",
      "BookingServicesDetailView",
      "BookingResourcesView",
      "BookingResourcesAddView",
      "BookingResourcesDetailView",
    ],
    minimumPermission: "site.booking.read",
    children: [
      {
        title: langs.get("view-title-booking-reservations"),
        icon: "CalendarCheck",
        route: {name: "BookingReservationsView", params: {siteName: siteName.value}},
        views: [
          "BookingReservationsView",
          "BookingReservationsAddView",
          "BookingReservationsDetailView",
        ],
        minimumPermission: "site.booking.read",
      },
      {
        title: langs.get("view-title-booking-slots"),
        icon: "CalendarClock",
        route: {name: "BookingSlotsView", params: {siteName: siteName.value}},
        views: [
          "BookingSlotsView",
          "BookingSlotsAddView",
          "BookingSlotsEditView",
          "BookingSlotsDetailView",
        ],
        minimumPermission: "site.booking.read",
      },
      {
        title: langs.get("view-title-booking-services"),
        icon: "ConciergeBell",
        route: {name: "BookingServicesView", params: {siteName: siteName.value}},
        views: [
          "BookingServicesView",
          "BookingServicesAddView",
          "BookingServicesEditView",
          "BookingServicesDetailView",
        ],
        minimumPermission: "site.booking.read",
      },
      {
        title: langs.get("view-title-booking-resources"),
        icon: "Album",
        route: {name: "BookingResourcesView", params: {siteName: siteName.value}},
        views: ["BookingResourcesView", "BookingResourcesAddView", "BookingResourcesDetailView"],
        minimumPermission: "site.booking.read",
      },
    ],
  },
  {
    title: langs.get("view-title-commerce-inventory"),
    icon: "Box",
    route: {name: "ShopInventoryView", params: {siteName: siteName.value}},
    views: ["ShopInventoryView", "ShopInventoryItemsDetailView", "ShopInventoryItemDetailView"],
    minimumPermission: "shop.inventory.read",
  },
  {
    title: langs.get("view-title-commerce-delivery-planning"),
    icon: "CalendarClock",
    route: {name: "ShopDeliveryPlanningView", params: {siteName: siteName.value}},
    views: ["ShopDeliveryPlanningView"],
    minimumPermission: "shop.deliveryplanning.read",
  },

  {
    title: langs.get("view-title-pages"),
    icon: "Earth",
    route: {name: "SitePagesView", params: {siteName: siteName.value}},
    views: ["SitePagesview", "SitePageSettingsView", "EditorView"],
    minimumPermission: "site.pages.read",
  },
  {
    title: langs.get("view-title-documents"),
    icon: "FileText",
    route: {name: "DocumentWizardView", params: {siteName: siteName.value}},
    views: [
      "DocumentsView",
      "DocumentWizardView",
      "DocumentLayoutsView",
      "DocumentLayoutAddView",
      "DocumentLayoutEditView",
      "DocumentTemplatesView",
      "DocumentTemplateAddView",
      "DocumentTemplateEditView",
      "DocumentsGeneratedView",
    ],
    minimumPermission: "shop.document.read",
    children: [
      {
        title: langs.get("view-title-documents-wizard"),
        icon: "Plus",
        route: {name: "DocumentWizardView", params: {siteName: siteName.value}},
        views: ["DocumentWizardView"],
        minimumPermission: "shop.document.create",
      },
      {
        title: langs.get("view-title-documents-layouts"),
        icon: "Layout",
        route: {name: "DocumentLayoutsView", params: {siteName: siteName.value}},
        views: ["DocumentLayoutsView", "DocumentLayoutAddView", "DocumentLayoutEditView"],
        minimumPermission: "shop.document.read",
      },
      {
        title: langs.get("view-title-documents-templates"),
        icon: "FileText",
        route: {name: "DocumentTemplatesView", params: {siteName: siteName.value}},
        views: ["DocumentTemplatesView", "DocumentTemplateAddView", "DocumentTemplateEditView"],
        minimumPermission: "shop.document.read",
      },
      {
        title: langs.get("view-title-documents-generated"),
        icon: "Download",
        route: {name: "DocumentsGeneratedView", params: {siteName: siteName.value}},
        views: ["DocumentsGeneratedView"],
        minimumPermission: "shop.document.download",
      },
    ],
  },
  {
    title: langs.get("cms"),
    icon: "Database",
    route: {name: "CmsEntriesView", params: {siteName: siteName.value}},
    views: [
      "CmsView",
      "CmsEntriesView",
      "CmsEntriesAddView",
      "CmsEntriesVersionDetailView",
      "CmsModelsView",
      "CmsModelsAddView",
      "CmsModelsDetailView",
      "CmsModelsFieldsAddView",
      "CmsModelsFieldsDetailView",
      "CmsModelsFieldsView",
      "CmsTagsAddView",
      "CmsTagsView",
      "CmsTokensAddView",
      "CmsTokensView",
    ],
    minimumPermission: "site.cms.read",
    children: [
      {
        title: "models",
        icon: "LayoutPanelTop",
        route: {name: "CmsModelsView", params: {siteName: siteName.value}},
        views: [
          "CmsModelsView",
          "CmsModelsAddView",
          "CmsModelsDetailView",
          "CmsModelsFieldsAddView",
          "CmsModelsFieldsDetailView",
          "CmsModelsFieldsView",
        ],
      },
      {
        title: "entries",
        icon: "Text",
        route: {name: "CmsEntriesView", params: {siteName: siteName.value}},
        views: ["CmsEntriesView", "CmsEntriesAddView", "CmsEntriesVersionDetailView"],
        minimumPermission: "site.cms.read",
      },
      {
        title: "tokens",
        icon: "Puzzle",
        route: {name: "CmsTokensView", params: {siteName: siteName.value}},
        views: ["CmsTokensView", "CmsTokensAddView"],
        minimumPermission: "site.cms.read",
      },
      {
        title: "tags",
        icon: "Tag",
        route: {name: "CmsTagsView", params: {siteName: siteName.value}},
        views: ["CmsTagsView", "CmsTagsAddView"],
        minimumPermission: "site.cms.read",
      },
    ],
  },
  {
    title: langs.get("crm"),
    icon: "BriefcaseBusiness",
    route: {name: "CrmView", params: {siteName: siteName.value}},
    views: [
      "CrmView",
      "CrmObjectView",
      "CrmMailingView",
      "CrmMailingSettingsView",
      "CrmMailingTemplatesView",
      "CrmMailingTemplatesAddView",
      "CrmMailingTemplatesDetailView",
      "CrmMailingQueueView",
      "CrmMailingWorkersView",
      "CrmMailingWorkersDetailView",
      "CrmImportView",
    ],
    minimumPermission: "site.crm.read",
    children: [
      {
        title: "objects",
        icon: "Briefcase",
        route: {name: "CrmView", params: {siteName: siteName.value}},
        views: ["CrmView", "CrmObjectView"],
        minimumPermission: "site.crm.read",
      },
      {
        title: "mailing templates",
        icon: "Mail",
        route: {name: "CrmMailingTemplatesView", params: {siteName: siteName.value}},
        views: [
          "CrmMailingView",
          "CrmMailingTemplatesView",
          "CrmMailingTemplatesAddView",
          "CrmMailingTemplatesDetailView",
        ],
        minimumPermission: "site.crm.read",
      },
      // {
      //   title: "mailing projects",
      //   icon: "Mail",
      //   route: {name: "CrmMailingTemplatesView", params: {siteName: siteName.value}},
      //   views: [
      //     "CrmMailingView",
      //     "CrmMailingTemplatesView",
      //     "CrmMailingTemplatesAddView",
      //     "CrmMailingTemplatesDetailView",
      //   ],
      //   minimumPermission: "site.crm.read",
      // },
      {
        title: "mailing workers",
        icon: "Mail",
        route: {name: "CrmMailingWorkersView", params: {siteName: siteName.value}},
        views: [
          "CrmMailingView",
          "CrmMailingWorkersView",
          // "CrmMailingWorkersAddView",
          "CrmMailingWorkersDetailView",
        ],
        minimumPermission: "site.crm.read",
      },
      {
        title: "mailing queue",
        icon: "Mail",
        route: {name: "CrmMailingQueueView", params: {siteName: siteName.value}},
        views: ["CrmMailingView", "CrmMailingQueueView"],
        minimumPermission: "site.crm.read",
      },
      {
        title: "import",
        icon: "CloudUpload",
        route: {name: "CrmImportView", params: {siteName: siteName.value}},
        views: ["CrmImportView"],
        minimumPermission: "site.crm.read",
      },
    ],
  },
  {
    title: langs.get("view-title-commerce-fee-reports"),
    icon: "AreaChart",
    route: {name: "ShopFeeReportsView", params: {siteName: siteName.value}},
    views: ["ShopFeeReportsView"],
    minimumPermission: "shop.fees.read",
  },
  ...(siteFeatures.value?.global?.shop?.promoCodesLegacy
    ? [
        {
          title: langs.get("view-title-commerce-promocodes"),
          icon: "CirclePercent",
          route: {name: "ShopPromoCodesView", params: {siteName: siteName.value}},
          views: ["ShopPromoCodesView", "ShopPromoCodeDetailView", "ShopPromoCodeImportView"],
          minimumPermission: "platform",
        },
      ]
    : []),
  ...(siteFeatures.value?.global?.shop?.giftCardsLegacy
    ? [
        {
          title: langs.get("view-title-commerce-giftcards"),
          icon: "Gift",
          route: {name: "ShopGiftCardsView", params: {siteName: siteName.value}},
          views: [
            "ShopGiftCardsView",
            "ShopGiftCardDetailView",
            "ShopGiftCardAddView",
            "ShopGiftCardTransactView",
          ],
          minimumPermission: "shop.giftcards.get",
        },
      ]
    : []),

  // migration handbook for shops migrated from v4 to v5a
  // platform admin only
  ...(siteFeatures.value?.global?.entitlement && !siteFeatures.value?.global?.shop?.giftCardsLegacy && isPlatformAdmin.value
    ? [
        {
          title: langs.get("view-title-commerce-giftcards"),
          icon: "Gift",
          route: {name: "ShopGiftCardsMigratedView", params: {siteName: siteName.value}},
          views: ["ShopGiftCardsMigratedView"],
          minimumPermission: "shop.giftcards.get",
        },
      ]
    : []),
  {
    title: langs.get("view-title-billing"),
    icon: "Wallet",
    route: {name: "BillingView", params: {siteName: siteName.value}},
    views: [
      "BillingView",
      "BillingInvoicesView",
      "BillingTopUpView",
      "BillingTransactionsView",
      "BillingMetersView",
    ],
    minimumPermission: "shop.fees.read",
    children: [
      {
        title: langs.get("view-title-billing-balance"),
        icon: "Wallet",
        route: {name: "BillingView", params: {siteName: siteName.value}},
        views: ["BillingView", "BillingTopUpView"],
        minimumPermission: "shop.fees.read",
      },
      {
        title: langs.get("view-title-billing-transactions"),
        icon: "Wallet",
        route: {name: "BillingTransactionsView", params: {siteName: siteName.value}},
        views: ["BillingTransactionsView"],
        minimumPermission: "shop.fees.read",
      },
      {
        title: langs.get("view-title-billing-meters"),
        icon: "Wallet",
        route: {name: "BillingMetersView", params: {siteName: siteName.value}},
        views: ["BillingMetersView"],
        minimumPermission: "shop.fees.read",
      },
      {
        title: langs.get("view-title-billing-invoices"),
        icon: "Wallet",
        route: {name: "BillingInvoicesView", params: {siteName: siteName.value}},
        views: ["BillingInvoicesView"],
        minimumPermission: "shop.fees.read",
      },
    ],
  },
  {
    title: langs.get("view-title-commerce-settings"),
    icon: "Cog",
    route: {name: "ShopSettingsView", params: {siteName: siteName.value}},
    views: [
      "ShopSettingsView",
      "ShopSettingsCartView",
      "ShopSettingsDeliveryMethodView",
      "ShopSettingsDeliveryView",
      "ShopSettingsGeneralView",
      "ShopSettingsInvoicingView",
      "ShopSettingsPaymentView",
      "AppsView",
      "AppsStripeView",
      "AppsStripeOnboardingView",
      "AppsStoryousView",
      "ShopSettingsChannelsView",
      "ShopSettingsChannelDetailView",
      "ShopSettingsEmailTemplatesView",
      "ShopSettingsEmailTemplateAddView",
      "ShopSettingsEmailTemplateDetailView",
    ],
    minimumPermission: "shop.config.read",
    children: [
      {
        title: langs.get("view-title-commerce-settings-general"),
        icon: "Cog",
        route: {name: "ShopSettingsGeneralView", params: {siteName: siteName.value}},
        views: ["ShopSettingsGeneralView"],
        minimumPermission: "shop.config.read",
      },
      {
        title: langs.get("view-title-commerce-settings-invoicing"),
        icon: "ReceiptText",
        route: {name: "ShopSettingsInvoicingView", params: {siteName: siteName.value}},
        views: ["ShopSettingsInvoicingView"],
        minimumPermission: "shop.config.read",
      },
      {
        title: langs.get("view-title-commerce-settings-payment"),
        icon: "HandCoins",
        route: {name: "ShopSettingsPaymentView", params: {siteName: siteName.value}},
        views: ["ShopSettingsPaymentView"],
        minimumPermission: "shop.config.read",
      },
      {
        title: langs.get("view-title-commerce-settings-delivery"),
        icon: "Truck",
        route: {name: "ShopSettingsDeliveryView", params: {siteName: siteName.value}},
        views: ["ShopSettingsDeliveryView", "ShopSettingsDeliveryMethodView"],
        minimumPermission: "shop.config.read",
      },
      {
        title: langs.get("view-title-commerce-settings-cart"),
        icon: "ShoppingCart",
        route: {name: "ShopSettingsCartView", params: {siteName: siteName.value}},
        views: ["ShopSettingsCartView"],
        minimumPermission: "shop.config.read",
      },
      {
        title: langs.get("view-title-commerce-settings-channels"),
        icon: "Rss",
        route: {name: "ShopSettingsChannelsView", params: {siteName: siteName.value}},
        views: ["ShopSettingsChannelsView", "ShopSettingsChannelDetailView"],
      },
      {
        title: langs.get("view-title-commerce-settings-emails"),
        icon: "Mail",
        route: {name: "ShopSettingsEmailTemplatesView", params: {siteName: siteName.value}},
        views: [
          "ShopSettingsEmailTemplatesView",
          "ShopSettingsEmailTemplateAddView",
          "ShopSettingsEmailTemplateDetailView",
        ],
        minimumPermission: "shop.config.read",
      },
      {
        title: langs.get("view-title-apps"),
        icon: "Blocks",
        route: {name: "AppsView"},
        views: ["AppsView", "AppsStripeView", "AppsStripeOnboardingView", "AppsStoryousView"],
        minimumPermission: "shop.config.read",
      },
    ],
  },
  {
    title: langs.get("view-title-commerce-export"),
    icon: "CloudDownload",
    route: {name: "ShopExportView", params: {siteName: siteName.value}},
    views: ["ShopExportView"],
    minimumPermission: "shop.config.read",
  },
  {
    title: langs.get("view-title-commerce-order-flow"),
    icon: "Workflow",
    route: {name: "ShopFlowView", params: {siteName: siteName.value}},
    views: ["ShopFlowView"],
    minimumPermission: "shop.config.read",
  },
  {
    title: langs.get("view-title-platform"),
    icon: "Bolt",
    route: {name: "PlatformView", params: {siteName: siteName.value}},
    views: [
      "PlatformView",
      "PlatformAddWizardView",
      "PlatformClientDetailView",
      "PlatformClientsView",
      "PlatformEventDetailView",
      "PlatformEventsView",
      "PlatformShopDetailView",
      "PlatformShopsView",
      "PlatformSiteDetailView",
      "PlatformSitesView",
      "PlatformSystemView",
      "PlatformUserAddView",
      "PlatformUserDetailView",
      "PlatformUsersView",
      "PlatformApiKeysView",
      "PlatformApiKeysDetailView",
      "PlatformApiKeysAddView",
      "PlatformApiScopesView",
      "PlatformApiScopesDetailView",
      "PlatformEntitlementsPassProjectsView",
      "PlatformEntitlementsPassApplePkiView",
      "PlatformOrgsView",
      "PlatformOrgsDetailView",
      "PlatformBillingView",
      "PlatformBillingBalancesView",
    ],
    minimumPermission: "platform",
    children: [
      {
        title: langs.get("view-title-platform-wizard"),
        icon: "Plus",
        route: {name: "PlatformAddWizardView", params: {siteName: siteName.value}},
        views: ["PlatformAddWizardView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-orgs"),
        icon: "Building",
        route: {name: "PlatformOrgsView", params: {siteName: siteName.value}},
        views: ["PlatformOrgsView", "PlatformOrgsDetailView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-clients"),
        icon: "Users",
        route: {name: "PlatformClientsView", params: {siteName: siteName.value}},
        views: ["PlatformClientsView", "PlatformClientDetailView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-sites"),
        icon: "Globe",
        route: {name: "PlatformSitesView", params: {siteName: siteName.value}},
        views: ["PlatformSitesView", "PlatformSiteDetailView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-shops"),
        icon: "ShoppingBag",
        route: {name: "PlatformShopsView", params: {siteName: siteName.value}},
        views: ["PlatformShopsView", "PlatformShopDetailView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-users"),
        icon: "User",
        route: {name: "PlatformUsersView", params: {siteName: siteName.value}},
        views: ["PlatformUsersView", "PlatformUserDetailView", "PlatformUserAddView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-billing-balances"),
        icon: "Wallet",
        route: {name: "PlatformBillingBalancesView", params: {siteName: siteName.value}},
        views: ["PlatformBillingBalancesView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-events"),
        icon: "Calendar",
        route: {name: "PlatformEventsView", params: {siteName: siteName.value}},
        views: ["PlatformEventsView", "PlatformEventDetailView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-api-keys"),
        icon: "Key",
        route: {name: "PlatformApiKeysView", params: {siteName: siteName.value}},
        views: ["PlatformApiKeysView", "PlatformApiKeysDetailView", "PlatformApiKeysAddView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-api-scopes"),
        icon: "Cog",
        route: {name: "PlatformApiScopesView", params: {siteName: siteName.value}},
        views: ["PlatformApiScopesView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-entitlements-pass-projects"),
        icon: "Gift",
        route: {name: "PlatformEntitlementsPassProjectsView", params: {siteName: siteName.value}},
        views: ["PlatformEntitlementsPassProjectsView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-entitlements-pass-apple-pki"),
        icon: "Gift",
        route: {name: "PlatformEntitlementsPassApplePkiView", params: {siteName: siteName.value}},
        views: ["PlatformEntitlementsPassApplePkiView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-platform-system"),
        icon: "Settings",
        route: {name: "PlatformSystemView", params: {siteName: siteName.value}},
        views: ["PlatformSystemView"],
        minimumPermission: "platform",
      },
    ],
  },
  {
    title: langs.get("view-title-tools"),
    icon: "PocketKnife",
    route: {name: "ToolsView", params: {siteName: siteName.value}},
    views: [
      "ToolsView",
      "ToolsPosEmulatorView",
      "ToolsQrGeneratorView",
      "ToolsLiftagoMockView",
      "ToolsQrScannerView",
    ],
    minimumPermission: "shop.tools.qrscanner",
    children: [
      {
        title: langs.get("view-title-tools-pos-emulator"),
        icon: "Calculator",
        route: {name: "ToolsPosEmulatorView", params: {siteName: siteName.value}},
        views: ["ToolsPosEmulatorView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-tools-qr-generator"),
        icon: "QrCode",
        route: {name: "ToolsQrGeneratorView", params: {siteName: siteName.value}},
        views: ["ToolsQrGeneratorView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-tools-liftago-mock"),
        icon: "Car",
        route: {name: "ToolsLiftagoMockView", params: {siteName: siteName.value}},
        views: ["ToolsLiftagoMockView"],
        minimumPermission: "platform",
      },
      {
        title: langs.get("view-title-tools-qr-scanner"),
        icon: "ScanLine",
        route: {name: "ToolsQrScannerView", params: {siteName: siteName.value}},
        views: ["ToolsQrScannerView"],
        minimumPermission: "shop.tools.qrscanner",
      },
    ],
  },
  // -----------------
  // {, params: {siteName: siteName.value}
])
const sidebarState = ref(localStorage.getItem("sidebarState") || "expanded")

const sidebarProjects = computed(() => {
  const projects = []
  // pinned
  sitesPinnedSorted.value.map((site) => {
    projects.push({
      id: site.id,
      title: site.config.title.cs || site.config.title.en || site.name,
      subtitle: site.domains[0] || site.name,
      active: site.id === userSelectedSiteId.value,
      site,
      // route: {name: "DashboardView", params: {siteName: site.name}},
    })
  })

  projects.push({
    id: "separator",
  })

  // unpinned
  sitesUnpinnedSorted.value.map((site) => {
    projects.push({
      id: site.id,
      title: site.config.title.cs || site.config.title.en || site.name,
      subtitle: site.domains[0] || site.name,
      active: site.id === userSelectedSiteId.value,
      site,
      // route: {name: "DashboardView", params: {siteName: site.name}},
    })
  })

  return projects
})

// floating menu config
const floatingMenu = computed(() => {
  const floatingMenuToInject = app.value.floatingMenu
  console.log({floatingMenuToInject: floatingMenuToInject.buttons})

  function getButtonLabel(button) {
    if (button.labelKey) {
      return langs.get(button.labelKey)
    }
    return button.label
  }

  const buttons = []

  if (route.meta?.floatingMenu?.buttons) {
    route.meta.floatingMenu.buttons.map((button) => {
      buttons.push({
        ...button,
        label: getButtonLabel(button),
      })
    })
  }

  if (floatingMenuToInject.buttons) {
    floatingMenuToInject.buttons.map((button) => {
      buttons.push({
        ...button,
        label: getButtonLabel(button),
      })
    })
  }

  const combinedFloatingMenu = {
    ...route.meta?.floatingMenu,
    buttons,
  }

  return combinedFloatingMenu || {}
})

const sidebarMenuItems = computed(() => {
  if (!userSelectedSiteId.value) {
    return []
  }
  const items = []
  const currentMenuItem = menuItems.value.find((item) => {
    return item.views.includes(route.name)
  })
  const currentMenuItemHasChildren = !!currentMenuItem?.children?.length

  // console.log({
  //   currentMenuItem,
  //   currentMenuItemHasChildren,
  //   currentRoute: route.name,
  //   siteName: siteName.value,
  // })

  // sub-routes
  if (currentMenuItemHasChildren) {
    // back
    items.push({
      title: langs.get("view-title-booking-back"),
      icon: "ArrowLeft",
      route: {name: "DashboardView", params: {siteName: siteName.value}},
    })
    currentMenuItem.children.forEach((child) => {
      const menuItem = {
        ...child,
        // dont include route when undefined
        route: siteName.value ? {name: child.route.name, params: {siteName: siteName.value}} : {},
        active: child.views.includes(route.name),
      }

      if (allowed(child.minimumPermission)) {
        items.push(menuItem)
      }
    })
  }
  // root
  else {
    menuItems.value.map((item) => {
      const menuItem = {
        ...item,
        // dont include route when undefined
        route: siteName.value ? {name: item.route.name, params: {siteName: siteName.value}} : {},
        active: item.views.includes(route.name),
      }

      if (allowed(item.minimumPermission)) {
        items.push(menuItem)
      }
    })
  }

  // console.log({items})
  return items
})

const viewAdminAttrs = computed(() => {
  if (["xShopOrdersView"].includes(route.name)) {
    return {
      mainScrollable: false,
      mainPadding: false,
    }
  } else {
    return {}
  }
})

const sidebarUser = computed(() => {
  return {
    title: user.value.name || "thy gracious name",
    subtitle: userAuth.value.email,
    imageURL: userAuth.value.photoURL,
  }
})

function onProjectSelected({project, e}) {
  // console.log("onProjectSelected", project, e)
  siteStore.onClick({site: project, e})
}

watch(
  userLoaded,
  (value) => {
    if (value) {
      // console.log("userLoaded", userLoaded.value)
      // console.log("userSelectedSiteId", userSelectedSiteId.value)
      // TODO: make this dynamic
      // set new org/space/env system (used for cms)
      // salabka (production | local_dev)
      // if (["CLPbVM5yMxX2xrBAsoCL", "x291zbwTyMOheOSYNKBj"].includes(userSelectedSiteId.value)) {
      //   console.log("setting salabka")
      //   app.value.orgId = "salabka"
      //   app.value.spaceId = "salabka-cz"
      //   app.value.environmentId = "main"
      // }
    }
  },
  {immediate: true}
)

watch(
  siteLoaded,
  async (value) => {
    if (value) {
      app.value.orgId = site.value.scope?.orgId
      app.value.spaceId = site.value.scope?.spaceId
      app.value.environmentId = site.value.scope?.environmentId
      // console.log("site loaded", site.value)
      await orgStore.bind()
      await orgSpacesStore.bind()
      orgSpacesEnvironmentsStore.bind()
    }
  },
  {immediate: true}
)

// TODO: remember selected space and environment in user settings
watch(
  orgSpacesLoaded,
  (value) => {
    if (value) {
      // console.log("org spaces loaded")

      // select first space
      app.value.spaceId = spaces.value?.[0]?.id
    }
  },
  {immediate: true}
)

watch(orgEnvironmentsLoaded, (value) => {
  if (value) {
    // console.log("org environments loaded")

    // select first environment
    app.value.environmentId = environments.value?.[0]?.id
  }
})
onMounted(() => {
  siteStore.bind()
})

function onClickResizeHandle(newValue) {
  sidebarState.value = newValue
  localStorage.setItem("sidebarState", newValue)
}
</script>

<template>
  <OneViewAdmin
    :sidebarProjects
    :sidebarMenuItems
    :sidebarUser
    :sidebarState
    :mainScrollable="
      !['CrmObjectView', 'ShopGiftCardsView', 'ShopOrdersListView', 'EditorView'].includes(
        $route.name
      )
    "
    :mainPadding="!['EditorView'].includes($route.name)"
    :floatingMenu
    v-bind="viewAdminAttrs"
    @clickResizeHandle="onClickResizeHandle"
    @projectSelected="onProjectSelected"
  >
    <template #logo>
      <div class="pb-4 pt-2" v-show="sidebarState === 'expanded'">
        <img :src="Logo" class="h-auto w-40 dark:hidden" />
        <img :src="LogoWhite" class="hidden h-auto w-40 opacity-50 dark:block" />
      </div>

      <div class="pb-6 pt-2" v-show="sidebarState === 'collapsed'">
        <img :src="LogoIcon" class="mx-auto h-auto w-4 dark:hidden" />
        <img :src="LogoIconWhite" class="mx-auto hidden h-auto w-4 opacity-50 dark:block" />
      </div>
    </template>

    <template #userPopup>
      <div class="p-0">
        <!-- mode -->
        <ColorSchemeToggle class="m-2" />

        <!-- profile -->
        <OneButton
          label="my profile"
          variant="primary"
          kind="naked"
          rounded="xl"
          align="start"
          fullWidth
          @action="router.push({name: 'UserView'})"
        />

        <!-- sign out -->
        <OneButton
          label="sign out"
          variant="error"
          kind="naked"
          rounded="xl"
          align="start"
          fullWidth
          @action="logout"
        />
      </div>
    </template>

    <template #main>
      <RouterView v-if="loaded" />
    </template>
  </OneViewAdmin>
  <FirebaseMessaging v-if="userLoaded" />
</template>
